/* Invoice panel: invoices completed on a chosen day whose Export pack hard copies are not printed yet (government submission). */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCheck, FileSpreadsheet, Loader2, Printer, RefreshCw } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { printPackNow } from "@/components/InvoiceDocExtras";

const istToday = () => new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 10);
const time = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export const DailyPrintReportCard = ({ refreshKey, onPrinted, isAdmin }) => {
  const [date, setDate] = useState(istToday);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    axios.get(`${API}/invoice-doc/print-report`, { params: { date }, headers: getAuthHeader() })
      .then(r => setD(r.data)).catch(e => toast.error(e.response?.data?.detail || "Could not load the print report"));
  }, [date]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const print = async (inv) => {
    setBusy(inv);
    try {
      const skipped = await printPackNow(inv);
      if (skipped.length) toast.warning(`${inv}: not ready, not printed — ${skipped.join(", ")}`, { duration: 8000 });
      load(); onPrinted?.();
    } catch (e) {
      let msg = e.message || "Could not print";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* keep */ }
      toast.error(msg);
    }
    setBusy("");
  };

  const summary = async () => {
    setBusy("xlsx");
    try {
      const r = await axios.get(`${API}/invoice-doc/print-summary.xlsx`, { params: { date }, headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `Print summary ${date}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not download the print summary"); }
    setBusy("");
  };

  const left = d?.unprinted.length || 0;
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${left ? "border-rose-300 bg-rose-50/40" : "border-emerald-200 bg-emerald-50/40"}`} data-testid="inv-print-report">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <ClipboardCheck className={`h-4 w-4 ${left ? "text-rose-600" : "text-emerald-600"}`} />
        <span className="font-semibold text-slate-800">Daily print report</span>
        <span className="text-slate-500">— completed invoices whose Export pack hard copies are not printed yet</span>
        <input type="date" value={date} max={istToday()} onChange={(e) => setDate(e.target.value || istToday())}
          className="ml-auto h-7 rounded border px-1.5 text-xs" data-testid="inv-print-report-date" />
        <button type="button" onClick={load} className="rounded p-1 text-slate-500 hover:bg-white" title="Refresh" data-testid="inv-print-report-refresh"><RefreshCw className="h-3.5 w-3.5" /></button>
        {isAdmin && (
          <button type="button" onClick={summary} disabled={!!busy} title="Excel of every export pack printed on this day (who, when, copies, reprints) — for government records"
            className="inline-flex items-center gap-1 rounded border border-indigo-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50" data-testid="inv-print-summary-btn">
            {busy === "xlsx" ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSpreadsheet className="h-3 w-3" />} Print summary</button>)}
      </div>
      {!d ? <Loader2 className="mt-2 h-4 w-4 animate-spin text-slate-400" /> : (
        <>
          <p className="mt-1 text-[11px] text-slate-600" data-testid="inv-print-report-summary">
            <b>{d.total}</b> completed · <b className="text-emerald-700">{d.printed}</b> printed · <b className={left ? "text-rose-700" : ""}>{left}</b> not printed</p>
          {left > 0 && (
            <div className="mt-2 max-h-64 overflow-y-auto rounded border bg-white">
              {d.unprinted.map(r => (
                <div key={r.invoice_no} className="flex items-center gap-3 border-b px-2 py-1 text-xs last:border-0" data-testid={`inv-print-report-row-${r.invoice_no}`}>
                  <span className="w-20 font-mono font-semibold text-indigo-700">{r.invoice_no}</span>
                  <span className="flex-1 truncate text-slate-600">{r.client_name}</span>
                  <span className="text-[10px] text-slate-400">done {time(r.completed_at)}</span>
                  {!r.invoice_made && <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700">invoice not made</span>}
                  <button type="button" disabled={!!busy} onClick={() => print(r.invoice_no)}
                    className="inline-flex items-center gap-1 rounded border border-emerald-600 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                    data-testid={`inv-print-report-print-${r.invoice_no}`}>
                    {busy === r.invoice_no ? <Loader2 className="h-3 w-3 animate-spin" /> : <Printer className="h-3 w-3" />} Print pack</button>
                </div>))}
            </div>)}
        </>)}
    </div>
  );
};
